import InterviewExam from '@/components/interview/InterviewExam';
import InterviewFeedback from '@/components/interview/InterviewFeedback';
import InterviewGradingWait from '@/components/interview/InterviewGradingWait';
import InterviewPreparingWait from '@/components/interview/InterviewPreparingWait';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { InterviewResponse, useApiClient } from '@/lib/api';
import { AlertTriangle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

// Main Page
const InterviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { api } = useApiClient();
  const [interview, setInterview] = useState<InterviewResponse | null>(null);
  const { interviewId } = useParams<{ interviewId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const normalizedStatus = interview?.status?.toUpperCase() ?? '';
  const isPreparing =
    normalizedStatus === 'CREATING' ||
    normalizedStatus === 'RESEARCHING' ||
    normalizedStatus === 'GENERATING';
  const isGrading = normalizedStatus === 'GRADING';
  const isIncomplete = normalizedStatus === 'INCOMPLETE';
  const isComplete = normalizedStatus === 'COMPLETE';
  const isFailed = normalizedStatus === 'FAILED';
  const hasKnownStatus =
    isPreparing || isGrading || isIncomplete || isComplete || isFailed;

  // Fetch interview data
  useEffect(() => {
    async function fetchInterview() {
      setLoading(true);
      try {
        if (!interviewId) return;
        const data = await api.getInterviewById(interviewId);
        setInterview(data);
      } catch (e) {
        toast.error("Could not load interview... Redirecting...");
        navigate('/interviews');
      } finally {
        setLoading(false);
      }
    }
    fetchInterview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  // Poll for GRADING status
  useEffect(() => {
    if (!interview || (!isPreparing && !isGrading)) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        if (!interviewId) return;
        const data = await api.getInterviewById(interviewId);
        if (!cancelled) {
          setInterview(data);
          const status = data.status.toUpperCase();
          if (
            status !== 'CREATING' &&
            status !== 'RESEARCHING' &&
            status !== 'GENERATING' &&
            status !== 'GRADING'
          ) {
            clearInterval(interval);
          }
        }
      } catch (e) {
        // Optionally show error
      }
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [interview, interviewId, api, isPreparing, isGrading]);

  return (
    <DashboardLayout>
      {!loading && interview && isPreparing && (
        <InterviewPreparingWait status={normalizedStatus} />
      )}
      {!loading && interview && isIncomplete && (
        <InterviewExam interview_response={interview} />
      )}
      {!loading && interview && isGrading && (
        <InterviewGradingWait />
      )}
      {!loading && interview && isComplete && (
        <InterviewFeedback interview={interview} />
      )}
      {!loading && interview && isFailed && (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-lg rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-700" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-red-950">
              I&apos;m sorry, something went wrong
            </h1>
            <p className="mt-2 text-sm text-red-900">
              We ran into an issue while preparing this interview, so it could not be generated.
            </p>
            <p className="mt-2 text-sm text-red-800">
              Please try creating a new interview. If this keeps happening, let us know.
            </p>
          </div>
        </div>
      )}
      {!loading && interview && !hasKnownStatus && (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-md rounded-2xl border bg-background p-8 text-center shadow-sm">
            <h1 className="text-xl font-semibold text-foreground">Interview unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This interview is in an unexpected state: <span className="font-medium text-foreground">{interview.status}</span>.
            </p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default InterviewPage;
